/**
 * Step 1 — Sources picker.
 *
 * Lists every skill that declares a `source.import` capability. For each
 * skill, renders a SourceCard with the readiness badge. The user picks a
 * source by clicking a Ready card; the wizard advances to Step 2 with
 * the chosen skill + capability locked in.
 */
import React from 'react';

import { useImportCapabilities } from '../../../../hooks/useImportCapabilities';
import type {
  ImportCapability,
  ImportCapabilitySkill,
} from '../../../../types/importCapabilities';
import SourceCard from './SourceCard';

interface SourceStepProps {
  onSelect: (skill: ImportCapabilitySkill, capability: ImportCapability) => void;
}

const SourceStep: React.FC<SourceStepProps> = ({ onSelect }) => {
  const { data, isLoading, error } = useImportCapabilities({ type: 'source.import' });

  if (isLoading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        Loading available sources…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
        Failed to load sources: {error instanceof Error ? error.message : 'unknown error'}
      </div>
    );
  }

  const skills = (data ?? []).filter((s) => s.capabilities.length > 0);

  if (skills.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
        No source skills are available yet. Add a skill declaring a
        `source.import` capability to its frontmatter to see it here.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-slate-900">Pick a source</h2>
        <p className="text-sm text-slate-600">
          Choose which system this collection should pull records from. Skills
          marked “Needs setup” require credentials in the Skills Library before
          you can continue.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {skills.flatMap((skill) =>
          skill.capabilities.map((capability) => (
            <SourceCard
              key={`${skill.skill_name}:${capability.capability_id}`}
              skill={skill}
              capability={capability}
              onSelect={onSelect}
            />
          )),
        )}
      </div>
    </div>
  );
};

export default SourceStep;
